import { Form, ToggleButton } from '@valid-eval/shared-react-components';
import { Controller } from 'react-hook-form';
import cx from 'classnames';

import FieldError from './FieldError';
import HelpText from './HelpText';
import Label from './Label';
import useFormFieldState from './useFormFieldState';

export type CheckboxListProps = {
  id?: string;
  label?: string;
  name: string;
  rules?: { validate?: Record<string, any> };
  disabled?: boolean;
  required?: boolean;
  helpText?: string;
  options: { value: string; label: string }[];
  toggleButton?: boolean;
  hideOnUnselected?: boolean;
  layout?: 'row' | 'column';
};

const CheckboxList = ({
  id,
  label,
  name,
  rules,
  disabled,
  required,
  helpText,
  options,
  toggleButton = false,
  hideOnUnselected = false,
  layout = 'column',
}: CheckboxListProps) => {
  const { control, isValid, isInvalid, error, validationClassname, getValues, clearErrors } =
    useFormFieldState(name);

  rules = rules || {};
  rules = required
    ? {
        ...rules,
        validate: {
          required() {
            // Required validation for checkbox list is defined here,
            // since it depends upon the value of all checkboxes in the list.
            const values = getValues();
            clearErrors(name); // Clear errors on the other checkboxes before validating
            return values[name]?.filter(Boolean).length > 0 ? undefined : 'Required';
          },
          ...rules.validate,
        },
      }
    : rules;

  const fieldId = `${name || id}-checkbox`;
  const value = getValues()?.[name] || [];

  return (
    <>
      <Form.Group className="mb-2">
        <Label htmlFor={fieldId} label={label} />
        <HelpText text={helpText} />
        <div
          className={toggleButton ? '' : `d-flex flex-${layout} ${layout === 'row' ? 'gap-4' : ''}`}
        >
          {options.map((option, index) => (
            <Controller
              key={option.value}
              control={control}
              name={`${name}.${index}`}
              rules={rules}
              defaultValue={false}
              render={({ field: { onChange, ...fieldProps } }) => {
                const checked = value.includes(option.value);
                if (toggleButton) {
                  return (
                    <ToggleButton
                      id={`${id}-${option.value}`}
                      className={cx('rounded btn-sm me-2 mb-3 text-nowrap', {
                        'd-none': hideOnUnselected && !checked,
                      })}
                      style={{
                        cursor: hideOnUnselected ? 'default' : 'pointer',
                      }}
                      type="checkbox"
                      variant={checked && !hideOnUnselected ? 'success' : 'default'}
                      checked={checked}
                      disabled={disabled}
                      onClick={hideOnUnselected ? (e) => e.preventDefault() : undefined}
                      onChange={(e) => onChange(e.currentTarget.checked ? option.value : false)}
                      {...fieldProps}
                    >
                      {checked && !hideOnUnselected && <i className="fa-regular fa-check" />}
                      <span className={hideOnUnselected ? 'text-black01' : ''}>{option.label}</span>
                      {hideOnUnselected && checked && (
                        <a
                          className="ms-2 p-0 m-0"
                          href={`#unselect-category-${option.value}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onChange(false);
                          }}
                        >
                          <i className="fa-solid fa-xmark p-0 m-0 text-gray03" />
                        </a>
                      )}
                    </ToggleButton>
                  );
                }
                return (
                  <Form.Check
                    id={`${id}-${option.value}`}
                    className={validationClassname}
                    label={option.label}
                    isInvalid={isInvalid}
                    isValid={isValid}
                    disabled={disabled}
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked ? option.value : false)}
                    {...fieldProps}
                  />
                );
              }}
            />
          ))}
        </div>
        {/* @ts-expect-error: Error object is an array in this case */}
        <FieldError show={isInvalid} message={String(error?.find(Boolean)?.message)} />
      </Form.Group>
    </>
  );
};

export default CheckboxList;
